import React, { useEffect, useRef } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Row, Col, Select } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import jaJP from 'antd/es/date-picker/locale/ja_JP'
import viVN from 'antd/es/date-picker/locale/vi_VN'
import HomeLayout from '../layouts/home'
import { SubmitButton, TextNormal } from '../../components'
import { FormDatePicker } from '../../components/form'
import { ViewingTime, ViewingHistory } from './components'
import { Wrapper, Heading, Filter } from './styled'
import { useCourseActivity, useProfile, useHistories } from '../../hooks'
import { STORAGE } from '../../utils'
import { DEFAULT_PAG } from './constants'

const locale = {
  vi: viVN,
  jp: jaJP
}

const DEFAULT_VALUE = {
  userId: null,
  completeStartTime: dayjs().startOf('month'),
  completeEndTime: dayjs().endOf('day')
}

const { Option } = Select

const CourseActivityScreen = () => {
  const { t, i18n: { language } } = useTranslation()
  const history = useHistories()
  const {
    userList,
    isUserListLoading,
    studyCourseTime,
    isStudyCourseTimeLoading,
    studyCourseHistory,
    isStudyCourseHistoryLoading,
    loadCourseActivityUserListAction,
    loadStudyCourseTimeAction,
    loadStudyCourseHistoryAction
  } = useCourseActivity()

  const form = useForm({ defaultValues: DEFAULT_VALUE })
  const { handleSubmit, setValue, getValues } = form

  const { profile } = useProfile()

  useEffect(() => {
    loadCourseActivityUserListAction()
    loadStudyCourseTimeAction({
      userId: profile?.userId,
      completeStartTime: DEFAULT_VALUE.completeStartTime.valueOf(),
      completeEndTime: DEFAULT_VALUE.completeEndTime.valueOf()
    })
    loadStudyCourseHistoryAction({
      userId: profile?.userId,
      completeStartTime: DEFAULT_VALUE.completeStartTime.valueOf(),
      completeEndTime: DEFAULT_VALUE.completeEndTime.valueOf(),
      limit: DEFAULT_PAG.limit,
      page: DEFAULT_PAG.page
    })
  }, [])

  const initialUserSet = useRef(false)
  useEffect(() => {
    if (!profile || userList.length === 0 || initialUserSet.current) return
    setValue('userId', profile.userId)
    initialUserSet.current = true
  }, [profile, userList, setValue])

  useEffect(() => {
    localStorage.setItem(STORAGE.SOURCE_PATH, history.location.pathname)
  }, [history.location.pathname])

  const onSubmit = (values) => {
    const { userId, completeStartTime, completeEndTime } = values
    loadStudyCourseTimeAction({
      userId,
      completeStartTime: completeStartTime.valueOf(),
      completeEndTime: completeEndTime.endOf('day').valueOf()
    })
    loadStudyCourseHistoryAction({
      userId,
      completeStartTime: completeStartTime.valueOf(),
      completeEndTime: completeEndTime.endOf('day').valueOf(),
      limit: DEFAULT_PAG.limit,
      page: DEFAULT_PAG.page
    })
  }

  return (
    <HomeLayout>
      <Wrapper>
        <div className="title">
          <Heading>
            {t('course_activity.title')}
          </Heading>
        </div>

        <FormProvider {...form}>
          <Filter>
            <Row gutter={[16, 0]}>
              <Col span={2}>
                <div className="label">
                  {t('course_activity.user')}
                </div>
              </Col>
              <Col span={10}>
                {profile?.isViewHistoryAuthority ? (
                  <Controller
                    name="userId"
                    control={form.control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        onChange={onChange}
                        loading={isUserListLoading}
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="選択"
                        optionFilterProp="children"
                        filterOption={(input, option) => option?.children.toLowerCase().includes(input.toLowerCase())}
                      >
                        {userList.map((user) => (
                          <Option key={user.key} value={user.value}>
                            {user.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <TextNormal>
                    {profile?.fullName}
                  </TextNormal>
                )}
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col span={2}>
                <div className="label">
                  {t('course_activity.period')}
                </div>
              </Col>
              <Col span={10}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FormDatePicker
                    t={t}
                    name="completeStartTime"
                    useDate
                    locale={locale[language]}
                    placeholder=""
                    allowClear={false}
                  />
                  ～
                  <FormDatePicker
                    t={t}
                    name="completeEndTime"
                    useDate
                    locale={locale[language]}
                    placeholder=""
                    allowClear={false}
                  />
                </div>
              </Col>
              <Col>
                <SubmitButton
                  title={t('common.search')}
                  fontWeight="fw_500"
                  fontSize="size_14"
                  onClick={handleSubmit(onSubmit)}
                />
              </Col>
            </Row>
          </Filter>
        </FormProvider>

        <ViewingTime t={t} time={studyCourseTime} isLoading={isStudyCourseTimeLoading} />
        <ViewingHistory
          t={t}
          profile={profile}
          history={studyCourseHistory}
          isLoading={isStudyCourseHistoryLoading}
          loadStudyCourseHistoryAction={loadStudyCourseHistoryAction}
          getValues={getValues}
        />
        {/* <ViewingRanking t={t} profile={profile} userRole={userRole} /> */}
      </Wrapper>
    </HomeLayout>
  )
}

export default CourseActivityScreen
